import { useState, useRef, useEffect, useCallback } from 'react';
import {
  useSendMessageMutation,
  useLazyGetConversationQuery,
  type ChatMessage,
  type ChatResponse,
} from '../store/api';
import { useWebSocketContext } from '../providers/WebSocketProvider';
import type { ExchangeEvent } from './useWebSocket';
import { extractErrorMessage } from '../utils/api';
import type { ThinkingEntry, PipelineStep, TaskEntry, Warning } from '../screens/ChatPlayground/ThinkingBlock';

function newSandboxSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export interface UsePlaygroundChatOptions {
  agentId: string;
  agentName?: string;
  /** When true, force-new-session flag is sent with the first POST. */
  forceNewOnFirstSend?: boolean;
  /** Callback invoked with the assistant text to speak (voice integration). */
  onSpeak?: (text: string) => void;
}

export interface UsePlaygroundChatReturn {
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  sessionId: string | undefined;
  setSessionId: React.Dispatch<React.SetStateAction<string | undefined>>;
  isProcessing: boolean;
  isSending: boolean;
  thinkingBlocks: ThinkingEntry[];
  pipelineSteps: PipelineStep[];
  tasks: TaskEntry[];
  responsePreview: string | null;
  warnings: Warning[];
  startTime: number | null;
  chatError: string | null;
  setChatError: React.Dispatch<React.SetStateAction<string | null>>;
  sendMessage: (text: string, opts?: { forceNew?: boolean }) => Promise<void>;
  newSession: () => void;
}

export function usePlaygroundChat({
  agentId,
  agentName,
  forceNewOnFirstSend = false,
  onSpeak,
}: UsePlaygroundChatOptions): UsePlaygroundChatReturn {
  const [postMessage, { isLoading: isSending }] = useSendMessageMutation();
  const [fetchConversation] = useLazyGetConversationQuery();
  const { isConnected, subscribe, unsubscribe, addListener, removeListener } =
    useWebSocketContext();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [thinkingBlocks, setThinkingBlocks] = useState<ThinkingEntry[]>([]);
  const [pipelineSteps, setPipelineSteps] = useState<PipelineStep[]>([]);
  const [chatError, setChatError] = useState<string | null>(null);
  const [tasks, setTasks] = useState<TaskEntry[]>([]);
  const [responsePreview, setResponsePreview] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<Warning[]>([]);

  const pendingConvIdRef = useRef<string | null>(null);
  const handledIdsRef = useRef<Set<string>>(new Set());
  const subscribedSessionRef = useRef<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const forceNewRef = useRef(forceNewOnFirstSend);
  const onSpeakRef = useRef(onSpeak);
  onSpeakRef.current = onSpeak;

  const exchangeEpochRef = useRef(0);
  const wsEpochRef = useRef(0);
  const syncHandledRef = useRef(false);

  useEffect(() => {
    return () => clearTimeout(pollTimerRef.current);
  }, []);

  const handleWsEvent = useCallback(
    (event: ExchangeEvent) => {
      const d = event.data || {};
      switch (event.type) {
        case 'exchange.started':
          if (syncHandledRef.current) break;
          wsEpochRef.current = exchangeEpochRef.current;
          setIsProcessing(true);
          setStartTime(Date.now());
          setThinkingBlocks([]);
          setPipelineSteps([]);
          setTasks([]);
          setResponsePreview(null);
          setWarnings([]);
          break;

        case 'exchange.thinking':
          setThinkingBlocks((prev) => [
            ...prev,
            {
              id: `t-${Date.now()}`,
              content: d.summary || d.content || d.text || '',
              timestamp: Date.now(),
            },
          ]);
          break;

        case 'exchange.step.started': {
          const rawStep = d.step || d.step_name || d.stepName || d.name || 'Step';
          const agentLabel = d.agent || d.agentName || d.agent_name;
          let stepName = rawStep;
          if (rawStep === 'fan_out') {
            const count = Array.isArray(d.agents) ? d.agents.length : '';
            stepName = count ? `Delegating to ${count} agents` : 'Fan-out';
          } else if (rawStep === 'fan_out_agent' && agentLabel) {
            stepName = agentLabel;
          }
          const stepId =
            rawStep === 'fan_out_agent' && agentLabel
              ? `fanout-${agentLabel}`
              : d.step_id || d.stepId || rawStep || `s-${Date.now()}`;
          setPipelineSteps((prev) => [
            ...prev,
            { id: stepId, name: stepName, status: 'started', startedAt: Date.now() },
          ]);
          break;
        }

        case 'exchange.step.completed': {
          const rawStepC = d.step || d.step_name || d.stepName;
          const agentLabelC = d.agent || d.agentName || d.agent_name;
          const stepKeyC =
            rawStepC === 'fan_out_agent' && agentLabelC
              ? `fanout-${agentLabelC}`
              : d.step_id || d.stepId || rawStepC;
          setPipelineSteps((prev) =>
            prev.map((s) =>
              (stepKeyC ? s.id === stepKeyC : s.status === 'started')
                ? { ...s, status: 'completed' as const, completedAt: Date.now() }
                : s,
            ),
          );
          break;
        }

        case 'exchange.step.failed': {
          const rawStepF = d.step || d.step_name || d.stepName;
          const agentLabelF = d.agent || d.agentName || d.agent_name;
          const stepKeyF =
            rawStepF === 'fan_out_agent' && agentLabelF
              ? `fanout-${agentLabelF}`
              : d.step_id || d.stepId || rawStepF;
          setPipelineSteps((prev) =>
            prev.map((s) =>
              (stepKeyF ? s.id === stepKeyF : s.status === 'started')
                ? { ...s, status: 'failed' as const, completedAt: Date.now() }
                : s,
            ),
          );
          break;
        }

        case 'exchange.subagent.started': {
          const target = (d.target_agent || d.targetAgent || 'sub-agent') as string;
          const stepId = `subagent-${target}`;
          setPipelineSteps((prev) => [
            ...prev,
            {
              id: stepId,
              name: `Delegated to ${target}`,
              status: 'started',
              startedAt: Date.now(),
            },
          ]);
          break;
        }

        case 'exchange.subagent.completed': {
          const targetC = (d.target_agent || d.targetAgent) as string | undefined;
          const stepKeyC = targetC ? `subagent-${targetC}` : null;
          setPipelineSteps((prev) =>
            prev.map((s) =>
              stepKeyC && s.id === stepKeyC
                ? { ...s, status: 'completed' as const, completedAt: Date.now() }
                : s,
            ),
          );
          break;
        }

        case 'exchange.subagent.failed': {
          const targetF = (d.target_agent || d.targetAgent) as string | undefined;
          const stepKeyF = targetF ? `subagent-${targetF}` : null;
          setPipelineSteps((prev) =>
            prev.map((s) =>
              stepKeyF && s.id === stepKeyF
                ? { ...s, status: 'failed' as const, completedAt: Date.now() }
                : s,
            ),
          );
          break;
        }

        case 'exchange.task.started': {
          const taskName = d.task_name || d.taskName || d.name || 'Task';
          const taskId = d.task_id || d.taskId || taskName || `task-${Date.now()}`;
          setTasks((prev) => [
            ...prev,
            { id: taskId, name: taskName, status: 'started', startedAt: Date.now() },
          ]);
          break;
        }

        case 'exchange.task.completed': {
          const taskNameC = d.task_name || d.taskName || d.name;
          const taskKeyC = d.task_id || d.taskId || taskNameC;
          setTasks((prev) =>
            prev.map((t) =>
              (taskKeyC ? t.id === taskKeyC : t.status === 'started')
                ? { ...t, status: 'completed' as const, completedAt: Date.now() }
                : t,
            ),
          );
          break;
        }

        case 'exchange.response.completed':
          setResponsePreview(d.preview || null);
          break;

        case 'exchange.error':
          setWarnings((prev) => [
            ...prev,
            {
              id: `w-${Date.now()}`,
              message: d.error || d.message || 'A non-fatal error occurred.',
              timestamp: Date.now(),
            },
          ]);
          break;

        case 'exchange.completed':
          if (syncHandledRef.current) break;
          if (wsEpochRef.current < exchangeEpochRef.current) break;
          if (pendingConvIdRef.current == null) {
            setIsProcessing(false);
          }
          break;

        case 'exchange.failed':
          setIsProcessing(false);
          pendingConvIdRef.current = null;
          setChatError(d.error || 'Agent processing failed.');
          break;

        case 'job.status': {
          const jobId = d.job_id || d.jobId || `job-${Date.now()}`;
          const rawStatus = String(d.status ?? 'pending').toLowerCase();
          const jobMessage = d.message || d.job_type || 'Job';
          const isDone =
            rawStatus === 'completed' || rawStatus === 'success' || rawStatus === 'done';
          const isFailed =
            rawStatus === 'failed' || rawStatus === 'error' || rawStatus === 'cancelled';
          const nextStatus: TaskEntry['status'] = isDone
            ? 'completed'
            : isFailed
              ? 'failed'
              : 'started';
          setTasks((prev) => {
            const existing = prev.find((t) => t.id === jobId);
            const now = Date.now();
            if (existing) {
              return prev.map((t) =>
                t.id === jobId
                  ? {
                      ...t,
                      name: jobMessage,
                      status: nextStatus,
                      completedAt: isDone || isFailed ? now : t.completedAt,
                    }
                  : t,
              );
            }
            return [
              ...prev,
              {
                id: jobId,
                name: jobMessage,
                status: nextStatus,
                startedAt: now,
                completedAt: isDone || isFailed ? now : undefined,
              },
            ];
          });
          break;
        }

        case 'conversation.result': {
          const result = event.data as Record<string, unknown> | undefined;
          const rawCid =
            (result?.conversation_id as string | undefined) ??
            (result?.conversationId as string | undefined) ??
            event.exchange_id;
          const pending = pendingConvIdRef.current;
          const convIdForDedup = rawCid ?? pending;

          if (!convIdForDedup) break;
          if (handledIdsRef.current.has(convIdForDedup)) break;
          if (pending != null && rawCid != null && rawCid !== pending) break;
          if (pending != null && rawCid == null && !result?.response) break;

          const finishWithAssistantMessage = (text: string) => {
            if (handledIdsRef.current.has(convIdForDedup)) return;
            handledIdsRef.current.add(convIdForDedup);
            setIsProcessing(false);
            pendingConvIdRef.current = null;
            setMessages((prev) => [...prev, { role: 'assistant', content: text }]);
            if (onSpeakRef.current && text) onSpeakRef.current(text);
          };

          if (result?.response) {
            finishWithAssistantMessage(String(result.response));
          } else if (
            result?.fetch_required &&
            result?.endpoint &&
            typeof result.endpoint === 'string'
          ) {
            const match = /\/conversations\/([^/]+)/.exec(result.endpoint);
            const fetchCid = match?.[1] ?? rawCid ?? pending;
            if (
              fetchCid &&
              !handledIdsRef.current.has(fetchCid) &&
              (pending == null || fetchCid === pending)
            ) {
              fetchConversation(fetchCid)
                .unwrap()
                .then((conv) => {
                  const responseContent =
                    conv.result?.response ?? (conv as { response?: string }).response;
                  if (!responseContent) return;
                  const text =
                    typeof responseContent === 'string'
                      ? responseContent
                      : JSON.stringify(responseContent);
                  if (handledIdsRef.current.has(fetchCid)) return;
                  handledIdsRef.current.add(fetchCid);
                  setIsProcessing(false);
                  pendingConvIdRef.current = null;
                  setMessages((prev) => [...prev, { role: 'assistant', content: text }]);
                  if (onSpeakRef.current && text) onSpeakRef.current(text);
                })
                .catch(() => {});
            }
          } else if (result?.s3Reference || result?.s3_reference || result?.conversationId) {
            const fetchCid =
              (result.conversationId as string | undefined) ??
              (result.conversation_id as string | undefined) ??
              pending;
            if (
              fetchCid &&
              !handledIdsRef.current.has(fetchCid) &&
              (pending == null || fetchCid === pending)
            ) {
              fetchConversation(fetchCid)
                .unwrap()
                .then((conv) => {
                  const responseContent =
                    conv.result?.response ?? (conv as { response?: string }).response;
                  if (!responseContent) return;
                  const text =
                    typeof responseContent === 'string'
                      ? responseContent
                      : JSON.stringify(responseContent);
                  if (handledIdsRef.current.has(fetchCid)) return;
                  handledIdsRef.current.add(fetchCid);
                  setIsProcessing(false);
                  pendingConvIdRef.current = null;
                  setMessages((prev) => [...prev, { role: 'assistant', content: text }]);
                  if (onSpeakRef.current && text) onSpeakRef.current(text);
                })
                .catch(() => {});
            }
          }
          break;
        }
      }
    },
    [fetchConversation],
  );

  useEffect(() => {
    addListener(handleWsEvent);
    return () => removeListener(handleWsEvent);
  }, [handleWsEvent, addListener, removeListener]);

  const pollConversation = useCallback(
    async (conversationId: string, attempt = 0) => {
      if (handledIdsRef.current.has(conversationId)) {
        setIsProcessing(false);
        pendingConvIdRef.current = null;
        return;
      }
      if (attempt > 10) {
        setIsProcessing(false);
        return;
      }
      try {
        const conv = await fetchConversation(conversationId).unwrap();
        if (conv.status === 'completed' && conv.result?.response) {
          if (handledIdsRef.current.has(conversationId)) {
            setIsProcessing(false);
            pendingConvIdRef.current = null;
            return;
          }
          handledIdsRef.current.add(conversationId);
          setIsProcessing(false);
          pendingConvIdRef.current = null;
          const responseContent = conv.result!.response;
          setMessages((prev) => [...prev, { role: 'assistant', content: responseContent }]);
          if (onSpeakRef.current && responseContent) onSpeakRef.current(responseContent);
        } else if (conv.status === 'processing') {
          const delay = Math.min(2000 * Math.pow(1.5, attempt), 15000);
          pollTimerRef.current = setTimeout(
            () => pollConversation(conversationId, attempt + 1),
            delay,
          );
        }
      } catch {
        const delay = Math.min(2000 * Math.pow(1.5, attempt), 15000);
        pollTimerRef.current = setTimeout(
          () => pollConversation(conversationId, attempt + 1),
          delay,
        );
      }
    },
    [fetchConversation],
  );

  const sendMessage = useCallback(
    async (text: string, opts?: { forceNew?: boolean }) => {
      if (!text.trim() || !agentId) return;

      exchangeEpochRef.current += 1;
      syncHandledRef.current = false;

      const userMsg: ChatMessage = { role: 'user', content: text };
      setMessages((prev) => [...prev, userMsg]);
      setChatError(null);
      setIsProcessing(true);
      setStartTime(Date.now());
      setThinkingBlocks([]);
      setPipelineSteps([]);
      setTasks([]);
      setResponsePreview(null);
      setWarnings([]);
      clearTimeout(pollTimerRef.current);

      let effectiveSessionId = sessionId;
      if (!effectiveSessionId) {
        effectiveSessionId = newSandboxSessionId();
        setSessionId(effectiveSessionId);
      }
      if (subscribedSessionRef.current !== effectiveSessionId) {
        subscribe(effectiveSessionId, agentName);
        subscribedSessionRef.current = effectiveSessionId;
      }

      try {
        const shouldForceNew = opts?.forceNew ?? forceNewRef.current ?? false;
        if (forceNewRef.current) forceNewRef.current = false;

        const result = await postMessage({
          query: text,
          agentId,
          sessionId: effectiveSessionId,
          forceNewSession: shouldForceNew,
          environment: 'sandbox',
        }).unwrap();

        if (result.sessionId) {
          setSessionId(result.sessionId);
          if (subscribedSessionRef.current !== result.sessionId) {
            subscribe(result.sessionId, agentName);
            subscribedSessionRef.current = result.sessionId;
          }
        }

        const res = result as ChatResponse & { status?: string };
        if (res.conversationId && res.status === 'processing') {
          pendingConvIdRef.current = res.conversationId;
          const initialDelay = isConnected ? 2000 : 1000;
          pollTimerRef.current = setTimeout(
            () => pollConversation(res.conversationId!),
            initialDelay,
          );
        } else if (result.message) {
          syncHandledRef.current = true;
          setIsProcessing(false);
          if (res.conversationId) {
            handledIdsRef.current.add(res.conversationId);
          }
          setMessages((prev) => [...prev, result.message]);
          if (onSpeakRef.current && result.message.content) {
            onSpeakRef.current(result.message.content);
          }
        }
      } catch (err) {
        setIsProcessing(false);
        setChatError(extractErrorMessage(err));
      }
    },
    [agentId, agentName, sessionId, isConnected, postMessage, subscribe, pollConversation],
  );

  const newSession = useCallback(() => {
    forceNewRef.current = forceNewOnFirstSend;
    setMessages([]);
    setSessionId(undefined);
    setThinkingBlocks([]);
    setPipelineSteps([]);
    setTasks([]);
    setResponsePreview(null);
    setWarnings([]);
    setIsProcessing(false);
    setStartTime(null);
    setChatError(null);
    pendingConvIdRef.current = null;
    handledIdsRef.current.clear();
    subscribedSessionRef.current = null;
    exchangeEpochRef.current = 0;
    wsEpochRef.current = 0;
    syncHandledRef.current = false;
    clearTimeout(pollTimerRef.current);
    unsubscribe();
  }, [unsubscribe, forceNewOnFirstSend]);

  return {
    messages,
    setMessages,
    sessionId,
    setSessionId,
    isProcessing,
    isSending,
    thinkingBlocks,
    pipelineSteps,
    tasks,
    responsePreview,
    warnings,
    startTime,
    chatError,
    setChatError,
    sendMessage,
    newSession,
  };
}
